import React from 'react';
import PolicyPageLayout, {
  PolicySection, Para, BulletList, BulletItem, InfoBox, WarnBox, ContactCard,
} from '../components/policy/PolicyPageLayout';

const toc = [
  { id: 'agreement',       label: 'Agreement to Terms' },
  { id: 'services',        label: 'Description of Service' },
  { id: 'account',         label: 'Account Registration' },
  { id: 'acceptable-use',  label: 'Acceptable Use' },
  { id: 'sending-limits',  label: 'Email Sending Limits' },
  { id: 'domain',          label: 'Domain Ownership' },
  { id: 'data-ownership',  label: 'Data & Content Ownership' },
  { id: 'billing',         label: 'Billing & Payments' },
  { id: 'cancellation',    label: 'Cancellation & Refunds' },
  { id: 'availability',    label: 'Service Availability' },
  { id: 'termination',     label: 'Suspension & Termination' },
  { id: 'liability',       label: 'Limitation of Liability' },
  { id: 'disclaimer',      label: 'Disclaimer of Warranties' },
  { id: 'indemnification', label: 'Indemnification' },
  { id: 'governing-law',   label: 'Governing Law' },
  { id: 'changes',         label: 'Changes to Terms' },
  { id: 'contact',         label: 'Contact' },
];

const related = [
  { label: 'Privacy Policy',         href: '/privacy-policy' },
  { label: 'Acceptable Use Policy',  href: '/acceptable-use-policy' },
  { label: 'Refund Policy',          href: '/refund-policy' },
  { label: 'Service Level Agreement',href: '/sla' },
];

const TermsOfServicePage: React.FC = () => (
  <PolicyPageLayout title="Terms of Service" lastUpdated="April 26, 2026" toc={toc} related={related}>

    <PolicySection id="agreement" number="1" title="Agreement to Terms">
      <Para>
        By accessing or using CruvzMail's email hosting platform ("Service") operated by Cruvz Online
        Shopping India Pvt Ltd ("we", "our", or "us"), you agree to be bound by these Terms of Service
        ("Terms"). If you are using the Service on behalf of an organization, you represent that you have
        the authority to bind that organization to these Terms.
      </Para>
      <Para>If you do not agree to these Terms, you must not access or use the Service.</Para>
    </PolicySection>

    <PolicySection id="services" number="2" title="Description of Service">
      <Para>CruvzMail provides a self-hosted business email hosting platform that includes:</Para>
      <BulletList>
        <BulletItem>Email sending and receiving via SMTP and IMAP protocols</BulletItem>
        <BulletItem>Web-based email client (webmail) with rich text composition and file attachments</BulletItem>
        <BulletItem>Custom domain email hosting with DNS management tools</BulletItem>
        <BulletItem>Admin panel for user, domain, and organization management</BulletItem>
        <BulletItem>Calendar, tasks, contacts, notes, and productivity tools</BulletItem>
        <BulletItem>AI-assisted email composition and summarization features</BulletItem>
        <BulletItem>Spam filtering, quarantine management, and email security</BulletItem>
        <BulletItem>DriveX file storage with WebDAV access and virus scanning</BulletItem>
      </BulletList>
    </PolicySection>

    <PolicySection id="account" number="3" title="Account Registration">
      <Para>
        To use the Service, you must create an account by providing accurate and complete information.
        You are responsible for:
      </Para>
      <BulletList>
        <BulletItem>Maintaining the confidentiality of your account credentials</BulletItem>
        <BulletItem>All activities that occur under your account</BulletItem>
        <BulletItem>Notifying us immediately of any unauthorized use at support@cruvzmail.com</BulletItem>
        <BulletItem>Ensuring contact information associated with your account remains current</BulletItem>
      </BulletList>
      <Para>
        We reserve the right to suspend or terminate accounts that contain inaccurate information or
        violate these Terms.
      </Para>
    </PolicySection>

    <PolicySection id="acceptable-use" number="4" title="Acceptable Use">
      <WarnBox>
        A violation of our Acceptable Use Policy is also a violation of these Terms and may result in
        immediate account suspension or termination without refund.
      </WarnBox>
      <Para>You agree to use the Service only for lawful purposes. You must not:</Para>
      <BulletList>
        <BulletItem>Send unsolicited bulk email (spam) or engage in any form of email abuse</BulletItem>
        <BulletItem>Transmit malware, viruses, or any malicious code via the Service</BulletItem>
        <BulletItem>Use the Service for phishing, fraud, impersonation, or deceptive activity</BulletItem>
        <BulletItem>Attempt to gain unauthorized access to any part of the Service or infrastructure</BulletItem>
        <BulletItem>Use the Service to harass, threaten, defame, or abuse others</BulletItem>
        <BulletItem>Distribute illegal content, including content that infringes intellectual property rights</BulletItem>
        <BulletItem>Interfere with or disrupt the Service or connected servers</BulletItem>
        <BulletItem>Resell or redistribute access to the Service without our written permission</BulletItem>
      </BulletList>
      <Para>
        Our full Acceptable Use Policy is available at <strong>/acceptable-use-policy</strong>.
      </Para>
    </PolicySection>

    <PolicySection id="sending-limits" number="5" title="Email Sending Limits">
      <Para>
        To protect our infrastructure and IP reputation, the following per-account limits apply:
      </Para>
      <BulletList>
        <BulletItem><strong>Daily send limit:</strong> 500 emails</BulletItem>
        <BulletItem><strong>Hourly send limit:</strong> 100 emails</BulletItem>
        <BulletItem><strong>Maximum recipients per email:</strong> 100</BulletItem>
        <BulletItem><strong>Maximum attachment size:</strong> 25 MB per email</BulletItem>
        <BulletItem><strong>Maximum attachments per email:</strong> 10 files</BulletItem>
      </BulletList>
      <Para>
        Higher limits are available on Enterprise plans. Exceeding limits may result in temporary
        sending restrictions.
      </Para>
    </PolicySection>

    <PolicySection id="domain" number="6" title="Domain Ownership">
      <Para>
        You must own or have authorized control over any domain you register with CruvzMail. Domain
        verification via DNS records is required before email services are activated. We reserve the
        right to disable services for domains that cannot be verified or are fraudulently registered.
      </Para>
    </PolicySection>

    <PolicySection id="data-ownership" number="7" title="Data and Content Ownership">
      <InfoBox>
        Your data is yours. We claim no ownership over the emails, files, contacts, or any other
        content you create or store using the Service.
      </InfoBox>
      <Para>
        You retain full ownership of all content you create, send, receive, or store using the Service.
        You grant us a limited license to process, store, and transmit your content solely to provide
        the Service. This license terminates when you delete your content or close your account.
      </Para>
    </PolicySection>

    <PolicySection id="billing" number="8" title="Billing and Payments">
      <Para>
        Paid plans are billed monthly or annually as selected during subscription. Payment is processed
        through Razorpay. By subscribing, you agree to:
      </Para>
      <BulletList>
        <BulletItem>Pay all applicable fees based on your selected plan</BulletItem>
        <BulletItem>Provide valid and current payment information</BulletItem>
        <BulletItem>Authorize recurring charges for subscription renewals</BulletItem>
      </BulletList>
      <Para>
        We reserve the right to modify pricing with at least 30 days advance notice. Price changes take
        effect at the start of your next billing cycle.
      </Para>
    </PolicySection>

    <PolicySection id="cancellation" number="9" title="Cancellation and Refunds">
      <Para>You may cancel your subscription at any time from the admin panel. Upon cancellation:</Para>
      <BulletList>
        <BulletItem>Your account remains accessible until the end of the current billing period</BulletItem>
        <BulletItem>No further charges will be applied after cancellation</BulletItem>
        <BulletItem>You may export your data before the account is deactivated</BulletItem>
        <BulletItem>Data will be permanently deleted 30 days after account deactivation</BulletItem>
      </BulletList>
      <Para>
        Our full refund policy is at <strong>/refund-policy</strong>. Contact billing@cruvzmail.com for
        refund requests.
      </Para>
    </PolicySection>

    <PolicySection id="availability" number="10" title="Service Availability">
      <Para>
        We target 99.9% monthly uptime for the Service. The Service may be temporarily unavailable due
        to scheduled maintenance (with advance notice where possible), emergency security patches, or
        events beyond our reasonable control. See our SLA at <strong>/sla</strong> for full details and
        credit entitlements.
      </Para>
    </PolicySection>

    <PolicySection id="termination" number="11" title="Suspension and Termination">
      <Para>We may suspend or terminate your access to the Service if:</Para>
      <BulletList>
        <BulletItem>You violate these Terms or our Acceptable Use Policy</BulletItem>
        <BulletItem>Your account is used for spam, fraud, or illegal activity</BulletItem>
        <BulletItem>Payment is overdue after three retry attempts</BulletItem>
        <BulletItem>We are required to do so by law, court order, or regulatory authority</BulletItem>
      </BulletList>
      <Para>
        Where possible, we will provide advance notice and opportunity to remedy the violation. For
        severe abuse (malware distribution, CSAM, active attacks), immediate termination without
        notice may occur.
      </Para>
    </PolicySection>

    <PolicySection id="liability" number="12" title="Limitation of Liability">
      <Para>
        To the maximum extent permitted by applicable law, CruvzMail shall not be liable for any
        indirect, incidental, special, consequential, or punitive damages — including loss of data,
        revenue, profits, or business opportunities — arising from your use of or inability to use the
        Service.
      </Para>
      <Para>
        Our total aggregate liability for any claim arising from the Service shall not exceed the amount
        you paid to us in the twelve (12) months preceding the claim.
      </Para>
    </PolicySection>

    <PolicySection id="disclaimer" number="13" title="Disclaimer of Warranties">
      <Para>
        The Service is provided "as is" and "as available" without warranties of any kind, either
        express or implied, including but not limited to implied warranties of merchantability, fitness
        for a particular purpose, or non-interruption.
      </Para>
      <Para>
        We do not warrant that the Service will be uninterrupted, timely, secure, or error-free at all
        times.
      </Para>
    </PolicySection>

    <PolicySection id="indemnification" number="14" title="Indemnification">
      <Para>
        You agree to indemnify and hold harmless CruvzMail, Cruvz Online Shopping India Pvt Ltd, its
        officers, employees, and agents from any claims, damages, losses, or expenses (including
        reasonable legal fees) arising from your use of the Service, your violation of these Terms, or
        your violation of any third-party rights.
      </Para>
    </PolicySection>

    <PolicySection id="governing-law" number="15" title="Governing Law and Jurisdiction">
      <Para>
        These Terms shall be governed by and construed in accordance with the laws of India, including
        the Indian Contract Act 1872, IT Act 2000, and Consumer Protection Act 2019. Any disputes
        arising from these Terms or the Service shall be subject to the exclusive jurisdiction of the
        courts in India.
      </Para>
    </PolicySection>

    <PolicySection id="changes" number="16" title="Changes to These Terms">
      <Para>
        We may update these Terms from time to time. We will notify registered users of material changes
        via email or an in-platform notice at least 30 days before the changes take effect. Continued
        use of the Service after changes constitutes acceptance of the updated Terms.
      </Para>
    </PolicySection>

    <PolicySection id="contact" number="17" title="Contact Us">
      <Para>If you have questions about these Terms of Service:</Para>
      <ContactCard>
        <strong>Legal inquiries:</strong> legal@cruvzmail.com<br />
        <strong>General support:</strong> support@cruvzmail.com<br />
        <br />
        <strong>CruvzMail</strong> — operated by Cruvz Online Shopping India Pvt Ltd<br />
        cruvzmail.com
      </ContactCard>
    </PolicySection>

  </PolicyPageLayout>
);

export default TermsOfServicePage;
